import { DataService } from "./data";
import {
  SharingRule,
  SharingRuleFormData,
  DRQValidationResult,
  SharingRuleMatchedUsersResponse,
} from "@/types/crm/sharing-rules";
import { AnyDRQQuery } from "@/utils/drq";
import { CRMObject } from "@/types/crm/permissions";

export class CRMSharingRulesService {
  /**
   * Récupérer la liste des objets configurables
   */
  static async getObjects(): Promise<CRMObject[]> {
    return DataService.client.get("/crm/app/objects/").json();
  }

  /**
   * Récupérer la liste des règles de partage
   */
  static async getSharingRules(referenceTypeId?: number): Promise<SharingRule[]> {
    if (referenceTypeId) {
      return DataService.client
        .get(`/crm/app/sharing-rules/by_reference_type/?reference_type_id=${referenceTypeId}`)
        .json();
    }
    const response: { count: number; results: SharingRule[] } = await DataService.client
      .get("/crm/app/sharing-rules/?page_size=1000")
      .json();
    return response.results;
  }

  /**
   * Créer une nouvelle règle de partage
   */
  static async createSharingRule(data: SharingRuleFormData): Promise<SharingRule> {
    return DataService.client.post(data, "/crm/app/sharing-rules/").json();
  }

  /**
   * Mettre à jour une règle de partage existante
   */
  static async updateSharingRule(id: number, data: Partial<SharingRuleFormData>): Promise<SharingRule> {
    return DataService.client.patch(data, `/crm/app/sharing-rules/${id}/`).json();
  }

  /**
   * Supprimer une règle de partage
   */
  static async deleteSharingRule(id: number): Promise<void> {
    await DataService.client.delete(`/crm/app/sharing-rules/${id}/`).res();
  }

  /**
   * Activer/Désactiver une règle de partage
   */
  static async toggleSharingRule(id: number): Promise<SharingRule> {
    return DataService.client.post({}, `/crm/app/sharing-rules/${id}/toggle_active/`).json();
  }

  /**
   * Valider un JSON DRQ côté serveur
   */
  static async validateDRQQuery(
    drqQuery: AnyDRQQuery, 
    referenceTypeId: number, 
    queryType: "user" | "object"
  ): Promise<DRQValidationResult> {
    return DataService.client
      .post(
        {
          drq_query: drqQuery,
          reference_type_id: referenceTypeId,
          query_type: queryType,
        },
        "/crm/app/sharing-rules/validate_drq/"
      )
      .json();
  }

  /**
   * Récupérer une règle de partage spécifique
   */
  static async getSharingRule(id: number): Promise<SharingRule> {
    return DataService.client.get(`/crm/app/sharing-rules/${id}/`).json();
  }

  /**
   * Récupérer les utilisateurs indexés pour une règle (index_status = indexed)
   */
  static async getMatchedUsers(id: number): Promise<SharingRuleMatchedUsersResponse> {
    return DataService.client.get(`/crm/app/sharing-rules/${id}/matched_users/`).json();
  }

  /**
   * Déclencher manuellement le recalcul de l'index pour une règle
   */
  static async reindex(id: number): Promise<void> {
    return DataService.client.post({}, `/crm/app/sharing-rules/${id}/reindex/`).json();
  }
}